import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useSignUpAction } from "@rhino-project/core/queries";

import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

type RegistrationForm = {
  email: string;
  organization: string;
  role: string;
  password: string;
  passwordConfirmation: string;
};

const initialForm: RegistrationForm = {
  email: "",
  organization: "",
  role: "applicant",
  password: "",
  passwordConfirmation: "",
};

export default function Register() {
  const [form, setForm] = useState<RegistrationForm>(initialForm);
  const [accepted, setAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();
  const { mutate: signUp, isPending, error } = useSignUpAction();

  const passwordsMatch = form.password === form.passwordConfirmation;
  const passwordIsLongEnough = form.password.length >= 8;
  const canSubmit = accepted && form.email && form.organization && passwordIsLongEnough && passwordsMatch && !isPending;

  const handleCreateAccount = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(false);

    if (!passwordIsLongEnough) {
      setFormError("Password must be at least 8 characters long.");
      return;
    }

    if (!passwordsMatch) {
      setFormError("Password and confirmation must match.");
      return;
    }

    setFormError("");
    signUp({
      email: form.email,
      password: form.password,
      password_confirmation: form.passwordConfirmation,
      name: form.organization,
    } as any, {
      onSuccess: () => { setSubmitted(true); navigate("/dual-use-on-ramp/intake"); },
      onError: () => setSubmitted(false),
    });
  };

  return (
    <div className="w-full min-w-0 overflow-x-hidden">
      <div className="flex w-full min-w-0 flex-col gap-6">
        <section className="space-y-4">
          <h1 className="text-2xl font-semibold">Create Account & Select Role</h1>
          <Card className="mx-auto w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Registration</CardTitle>
              <CardDescription>Choose your pathway and create credentials you can use to sign in later.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleCreateAccount}>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required autoComplete="email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org">Organization</Label>
                  <Input id="org" name="organization" value={form.organization} onChange={(event) => setForm({ ...form, organization: event.target.value })} required autoComplete="organization" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={(event) => setForm({ ...form, password: event.target.value })}
                      placeholder="At least 8 characters"
                      minLength={8}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="passwordConfirmation">Confirm password</Label>
                    <Input
                      id="passwordConfirmation"
                      name="passwordConfirmation"
                      type="password"
                      value={form.passwordConfirmation}
                      onChange={(event) => setForm({ ...form, passwordConfirmation: event.target.value })}
                      placeholder="Re-enter password"
                      minLength={8}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Keep this password safe. You will need it if you sign out and return later.</p>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                    <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent className="bg-popover text-popover-foreground">
                      <SelectItem value="applicant">Applicant</SelectItem>
                      <SelectItem value="sponsor">Sponsor</SelectItem>
                      <SelectItem value="navigator">Navigator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="terms" checked={accepted} onCheckedChange={(value) => setAccepted(value === true)} />
                  <Label htmlFor="terms">I agree to platform terms</Label>
                </div>
                <Button className="w-full" type="submit" disabled={!canSubmit}>{isPending ? "Creating..." : "Create account"}</Button>
                {submitted && <p className="text-sm text-primary">Account created. Continue through the guided intake.</p>}
                {formError && <p className="text-sm text-destructive">{formError}</p>}
                {error && <p className="text-sm text-destructive">Account creation failed. Please check your details and try again.</p>}
              </form>
            </CardContent>
          </Card>
        </section>
        <section>
          <Alert>
            <Badge className="mr-2">Guidance</Badge>
            <AlertDescription>
              Dual-use applicants receive guided intake, while sponsors and navigators receive dashboard access.
            </AlertDescription>
          </Alert>
        </section>
      </div>
    </div>
  );
}